import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { formatDateTime } from '../../../helpers/formatDateTime';
import { getAlarmSections, alarmSectionsToText, alarmSectionsToHtml } from '../../../helpers/formatAlarmClipboard';
import { StatusMessage, SearchQuery } from '../../ui';
import { useBureauCategory } from '../../../hooks/useBureauCategory';
import { useDashboardCounts } from '../../../hooks/useDashboardCounts';
import BureauFilter from '../../filters/BureauFilter';
import { FilterBtn, GridViewBtn, ListViewBtn } from '../../ui/Buttons';
import { BureauCategoryTable } from './views/BureauCategoryTable';
import { BureauCategoryCards } from './views/BureauCategoryCards';
import Pagination from '../../ui/Pagination';
import BulkActionToolbar from '../../ui/BullActionToolbar';
import BureauAcknowledgeForm from '../../forms/BureauAcknowledgeForm';
import Modal from '../../ui/Modal';
import type { BureauAlarm } from '../../../services/bureauManagement/bureauManagement.types';
import { getBureauCategoryLabel, ACTIVE_NOT_ACK, ACTIVE_ACK, NOT_ACTIVE_NOT_ACK } from './constants';
import { localAcknowledge, getBureauAlarmsByCategory } from '../../../services/bureauManagement/bureauManagement.service';
import { getProfile } from '../../../services/userManagement/userManagement.service';
import { useAuthContext } from '../../../contexts/useAuthContext';
import LoadingSpinner from '../../ui/LoadingSpinner';
import { alarmAcknowledgedEvent } from '../../../services/events/alarmEvents';
import { TOAST_SUCCESS_MS, TOAST_ERROR_MS } from '../../../constants/timeouts';
import { useBureauSignalRContext } from '../../../contexts/BureauSignalRContext';

interface Props {
  searchQuery?: string;
  onSearchChange?: (q: string) => void;
  initialCategory?: string;
  onSelect?: (item: BureauAlarm) => void;
  onCategoryChange?: (cat: string) => void;
  hideSearch?: boolean;
  hideFilter?: boolean;
  hideToolbar?: boolean;
  tableOnly?: boolean;
  siteScope?: {
    siteIds?: number[];
    siteGuids?: string[];
    siteIdentifiers?: string[];
  } | null;
  useClientPagination?: boolean;
  tableHeaderActions?: React.ReactNode;
  tableHeaderSubtitle?: React.ReactNode;
  showSortSummary?: boolean;
}

export const BureauCategory: React.FC<Props> = ({ initialCategory = ACTIVE_NOT_ACK, onSelect, searchQuery = '', onSearchChange, onCategoryChange, hideSearch = false, hideFilter = false, hideToolbar = false, tableOnly = false, siteScope = null, useClientPagination = false, tableHeaderActions, tableHeaderSubtitle, showSortSummary = false }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>(initialCategory);
  // Keep internal selectedCategory in sync when parent updates initialCategory
  useEffect(() => {
    if (initialCategory !== selectedCategory) {
      setSelectedCategory(initialCategory || 'all');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCategory]);
  const filterBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const { lastNotification } = useBureauSignalRContext();
  const handleCategoryChange = (cat: string) => {
    setSelectedCategory(cat);
    setSortColumn('');
    setSortOrder('asc');
    applySort(undefined, undefined);
    if (typeof onCategoryChange === 'function') {onCategoryChange(cat);}
    // close dropdown and return focus to the Filter button without scrolling
    setIsFilterOpen(false);
    if (filterBtnRef.current) {
      try {
        filterBtnRef.current.focus({ preventScroll: true });
      } catch (_e) {
        filterBtnRef.current.focus();
      }
    }
  };
  const [gridView, setGridView] = useState(false);
  const showGridView = gridView && !tableOnly;
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [acknowledgedFilter, setAcknowledgedFilter] = useState<string[]>([]);
  const [selectedIds, setSelectedIds] = useState<Record<number, boolean>>({});
  const [bulkMode, setBulkMode] = useState<'acknowledge' | null>(null);
  const [actionInFlight, setActionInFlight] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  // IDs acknowledged locally this session — kept permanently so the alarm stays hidden
  // even if the backend still returns localAck:false after refresh.
  const [locallyAckedIds, setLocallyAckedIds] = useState<Set<number>>(new Set());

  const { userProfile } = useAuthContext();
  const SERVER_PAGE_SIZE = 100;
  const hasSiteScope = siteScope !== null;
  const {
    data,
    allData,
    loading,
    error,
    refresh,
    totalCount,
    counts,
    page,
    totalPages,
    goToPage,
    applySort,
  } = useBureauCategory(selectedCategory, SERVER_PAGE_SIZE, hasSiteScope);
  const { counts: dashboardCounts } = useDashboardCounts();
  const [statsCounts, setStatsCounts] = useState<Record<string, number> | null>(null);
  useEffect(() => {
    const cats = [ACTIVE_NOT_ACK, ACTIVE_ACK, NOT_ACTIVE_NOT_ACK] as const;
    void Promise.all(cats.map(cat => getBureauAlarmsByCategory(cat, 1, 1)))
      .then(responses => {
        const merged: Record<string, number> = {};
        responses.forEach((res, idx) => {
          const cat = cats[idx];
          if (!res?.success || !res.data) { return; }
          // Get categoryCounts from the server — this is what correctly, shows true count
          const cc = res.data.categoryCounts;
          merged[cat] = cc?.[cat] ?? res.data.totalCount ?? 0;
        });
        setStatsCounts(merged);
      })
      .catch(() => { /* counts will appear once category data loads */ });
  }, []);

  // Auto-refresh when a bureau alarm notification arrives
  useEffect(() => {
    if (lastNotification) {
      refresh();
      // Also refetch category counts to ensure heading totals update
      const cats = [ACTIVE_NOT_ACK, ACTIVE_ACK, NOT_ACTIVE_NOT_ACK] as const;
      void Promise.all(cats.map(cat => getBureauAlarmsByCategory(cat, 1, 1)))
        .then(responses => {
          const merged: Record<string, number> = {};
          responses.forEach((res, idx) => {
            const cat = cats[idx];
            if (!res?.success || !res.data) { return; }
            const cc = res.data.categoryCounts;
            merged[cat] = cc?.[cat] ?? res.data.totalCount ?? 0;
          });
          setStatsCounts(merged);
        })
        .catch(() => { /* ignore errors during refresh */ });
    }
  }, [lastNotification, refresh]);

  // Also refresh on CustomerAlarmTreeUpdated (fires when site not in user's CSH group)
  useEffect(() => {
    return alarmAcknowledgedEvent.subscribe(payload => {
      if (payload?.source === 'signalr' && payload?.eventType === 'BureauAlarmUpdated') {
        return;
      }
      refresh();
      const cats = ['active-not-ack', 'active-ack', 'not-active-not-ack'] as const;
      void Promise.all(cats.map(cat => getBureauAlarmsByCategory(cat, 1, 1)))
        .then(responses => {
          const merged: Record<string, number> = {};
          responses.forEach((res, idx) => {
            const cat = cats[idx];
            if (!res?.success || !res.data) { return; }
            const cc = res.data.categoryCounts;
            merged[cat] = cc?.[cat] ?? res.data.totalCount ?? 0;
          });
          setStatsCounts(merged);
        })
        .catch(() => { /* ignore */ });
    });
  }, [refresh]);

  const anySelected = Object.values(selectedIds || {}).some(v => v === true);

  const acknowledgeSelected = () => {
    const selected = filteredData.filter(alarm => !!selectedIds[alarm.bureauAlarmId || alarm.id || 0]);
    if (!selected || selected.length === 0) {return;}
    setBulkMode('acknowledge');
  };

  const handleBulkCopy = async () => {
    const selected = selectedAlarms;
    if (!selected || selected.length === 0) {return;}

    try {
      const alarmsData = selected.map(alarm => {
        const acknowledgedByName = (alarm as Record<string, unknown>).acknowledgedByName as string | null
          || (alarm as Record<string, unknown>).localAckByName as string | null
          || (alarm as Record<string, unknown>).LocalAckByName as string | null
          || null;
        const sections = getAlarmSections(alarm, acknowledgedByName);
        return { text: alarmSectionsToText(sections), html: alarmSectionsToHtml(sections) };
      });

      const final = alarmsData.map(d => d.text).join('\n\n---\n\n');
      const finalHtml = alarmsData.map(d => d.html).join('<hr/>');

      // Try Clipboard API with text/html and text/plain; fallback to textarea/contentEditable execCommand copy
      try {
        if (navigator.clipboard?.write) {
          const blobPlain = new Blob([final], { type: 'text/plain' });
          const blobHtml = new Blob([finalHtml], { type: 'text/html' });
          const item = new ClipboardItem({ 'text/plain': blobPlain, 'text/html': blobHtml });
          await navigator.clipboard.write([item]);
        } else if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(final);
        } else {
          // Fallback: use a contentEditable div to copy HTML
          const div = document.createElement('div');
          div.style.position = 'fixed';
          div.style.left = '-9999px';
          div.contentEditable = 'true';
          div.innerHTML = finalHtml;
          document.body.appendChild(div);
          const range = document.createRange();
          range.selectNodeContents(div);
          const sel = window.getSelection();
          sel?.removeAllRanges();
          sel?.addRange(range);
          (document as any).execCommand?.('copy');
          sel?.removeAllRanges();
          document.body.removeChild(div);
        }
      } catch (err) {
        // last resort: plain text
          console.info('Failed to copy with HTML formatting, falling back to plain text', err);
        try {
          await navigator.clipboard.writeText(final);
        } catch (_) {
          const ta = document.createElement('textarea');
          ta.value = final;
          ta.style.position = 'fixed';
          ta.style.left = '-9999px';
          document.body.appendChild(ta);
          ta.select();
          const anyDoc: any = document;
          if (anyDoc.execCommand) {anyDoc.execCommand('copy');}
          document.body.removeChild(ta);
        }
      }

      setSuccessMessage(`Copied ${selected.length} alarm(s) to clipboard`);
      setTimeout(() => setSuccessMessage(null), TOAST_SUCCESS_MS);
    } catch (err) {
      console.info('Failed to copy selected alarms', err);
      setErrorMessage('Failed to copy selected alarms');
      setTimeout(() => setErrorMessage(null), TOAST_ERROR_MS);
    }
  };

  const handleToggleSelect = (id: number) => {
    setSelectedIds(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleToggleSelectAll = () => {
    const selectionPool = useClientPagination ? visibleData : filteredData;
    if (selectionPool.every(item => selectedIds[(item.bureauAlarmId || item.id) ?? 0])) {
      // Unselect all
      setSelectedIds({});
    } else {
      // Select all
      const newSelected: Record<number, boolean> = {};
      selectionPool.forEach(item => {
        const id = (item.bureauAlarmId || item.id) ?? 0;
        if (id) {newSelected[id] = true;}
      });
      setSelectedIds(newSelected);
    }
  };

  const handleBulkAcknowledge = async (alarms: BureauAlarm[]) => {
    // Fetch the current user's profile to get their GUID
    const profileResponse = await getProfile();
    
    if (!profileResponse?.success || !profileResponse.data?.id) {
      throw new Error('Unable to retrieve user profile');
    }

    const acknowledgedBy = profileResponse.data.id;
    const userName = userProfile?.preferred_username || userProfile?.email || userProfile?.name || 'Unknown User';

    const payload = {
      acknowledgedBy: acknowledgedBy,
      userName: userName,
    };

    // Acknowledge each alarm sequentially
    const successfulIds: number[] = [];
    for (const alarm of alarms) {
      const alarmId = alarm.bureauAlarmId || alarm.id;
      if (!alarmId) {continue;}
      
      setActionInFlight(String(alarmId));
      
      try {
        const response = await localAcknowledge(alarmId, payload);
        if (!response?.success) {
          console.info(`Failed to acknowledge alarm ${alarmId}:`, response?.error);
        } else {
          successfulIds.push(alarmId);
          // Emit event for each successful acknowledgment
          alarmAcknowledgedEvent.emit();
        }
      } catch (err) {
        console.info(`Error acknowledging alarm ${alarmId}:`, err);
      }
    }

    // Permanently hide successfully acknowledged alarms from this view
    if (successfulIds.length > 0) {
      setLocallyAckedIds(prev => {
        const next = new Set(prev);
        successfulIds.forEach(id => next.add(id));
        return next;
      });
    }

    setActionInFlight(null);
    
    // Refresh data and clear selections
    await refresh();
    setBulkMode(null);
    setSelectedIds({});

    // Show success message with site names of acknowledged alarms
    if (successfulIds.length > 0) {
      const successfulAlarms = alarms.filter(a => successfulIds.includes(a.bureauAlarmId || a.id || 0));
      const siteNames = [...new Set(successfulAlarms.map(a => a.siteName || a.siteIdentifier || String(a.bureauAlarmId || a.id)))].join(', ');
      setSuccessMessage(`Acknowledged ${successfulIds.length} alarm(s): ${siteNames}`);
      setTimeout(() => setSuccessMessage(null), TOAST_SUCCESS_MS);
    }
  };

  const handleRemoveBulkSelection = (id: number) => {
    setSelectedIds(prev => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  };


  const handleClearFilter = () => {
    setStartDate(null);
    setEndDate(null);
    setStatusFilter([]);
    setAcknowledgedFilter([]);
    setSelectedCategory(ACTIVE_NOT_ACK);
  };

  const isFilterActive = Boolean(
    selectedCategory !== ACTIVE_NOT_ACK ||
    startDate ||
    endDate ||
    statusFilter.length > 0 ||
    acknowledgedFilter.length > 0
  );

  const scopedSourceData = useMemo(() => {
    if (hasSiteScope && Array.isArray(allData) && allData.length > 0) {
      return allData;
    }
    return data;
  }, [hasSiteScope, allData, data]);

  const normalizedScopeIds = useMemo(() => new Set((siteScope?.siteIds || []).map(id => Number(id)).filter(id => Number.isFinite(id) && id > 0)), [siteScope]);
  const normalizedScopeGuids = useMemo(() => new Set((siteScope?.siteGuids || []).map(v => String(v).trim().toLowerCase()).filter(Boolean)), [siteScope]);
  const normalizedScopeIdentifiers = useMemo(() => new Set((siteScope?.siteIdentifiers || []).map(v => String(v).trim().toLowerCase()).filter(Boolean)), [siteScope]);

  const isSourceStateActive = (alarm: BureauAlarm): boolean => {
    const source = String(alarm.sourceAlarmState || '').trim().toLowerCase();
    if (!source) { return true; }
    return source === 'active';
  };

  // Apply filters to the data
  const filteredData = useMemo(() => {
    let result = [...scopedSourceData];

    if (hasSiteScope) {
      result = result.filter(item => {
        const row = item as BureauAlarm & {
          sirId?: number;
          siteIdentifierRegistryId?: number;
          siteId?: number;
        };
        const candidateId = Number(row.siteRegistryId ?? row.sirId ?? row.siteIdentifierRegistryId ?? row.siteId ?? 0);
        if (Number.isFinite(candidateId) && candidateId > 0 && normalizedScopeIds.has(candidateId)) {
          return true;
        }
        const guid = String(row.siteGuid || '').trim().toLowerCase();
        if (guid && normalizedScopeGuids.has(guid)) {
          return true;
        }
        const identifier = String(row.siteIdentifier || '').trim().toLowerCase();
        if (identifier && normalizedScopeIdentifiers.has(identifier)) {
          return true;
        }
        return false;
      });
    }

    // Category filter - ensure we only show alarms matching the selected category
    if (selectedCategory && selectedCategory !== 'all') {
      result = result.filter(item => {
        const state = item.bureauAlarmState || '';
        
        switch (selectedCategory) {
          case ACTIVE_NOT_ACK:
            return state === 'ActiveAlarmsNotAck' && !item.localAck && isSourceStateActive(item);
          case 'active-ack':
            return state === 'ActiveAlarmsAck';
          case 'not-active-not-ack':
            return state === 'NotActiveAlarmsNotAck';
          default:
            return true;
        }
      });
    }

    // Search query filter
    if (searchQuery && searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      result = result.filter(item => {
        // Search across multiple fields
        const searchableFields = [
          item.siteName,
          item.siteIdentifier,
          item.pathToAlarm,
          item.path,
          item.alarmIdentifier,
          item.bureauRecipientName,
          item.bureauName,
          item.bureauEndpoint,
          item.bureauAlarmState,
          String(item.priority || ''),
        ];
        
        return searchableFields.some(field => 
          field && String(field).toLowerCase().includes(query)
        );
      });
    }

    // Date range filter
    if (startDate || endDate) {
      result = result.filter(item => {
        const itemDate = new Date(item.lastOccurrenceAt || '');
        if (startDate && itemDate < new Date(startDate)) {
          return false;
        }
        if (endDate && itemDate > new Date(endDate)) {
          return false;
        }
        return true;
      });
    }

    // Status filter
    if (statusFilter.length > 0) {
      result = result.filter(item =>
        statusFilter.includes(item.bureauAlarmState || '')
      );
    }

    // Acknowledged filter
    if (acknowledgedFilter.length > 0) {
      result = result.filter(item => {
        const isAck = item.localAck ? 'acknowledged' : 'unacknowledged';
        return acknowledgedFilter.includes(isAck);
      });
    }

    // Hide alarms acknowledged this session only from the Unacknowledged view (backend may lag on localAck)
    if (selectedCategory === ACTIVE_NOT_ACK && locallyAckedIds.size > 0) {
      result = result.filter(item => {
        const id = (item.bureauAlarmId || item.id) ?? 0;
        return !locallyAckedIds.has(id);
      });
    }

    return result;
  }, [scopedSourceData, hasSiteScope, normalizedScopeIds, normalizedScopeGuids, normalizedScopeIdentifiers, searchQuery, startDate, endDate, statusFilter, acknowledgedFilter, selectedCategory, locallyAckedIds]);

  const [clientPage, setClientPage] = useState<number>(1);
  useEffect(() => {
    if (useClientPagination) {
      setClientPage(1);
    }
  }, [useClientPagination, selectedCategory, searchQuery, hasSiteScope]);

  // Server sort state (UI state kept for sort indicators)
  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const mapSortColumnToServer = (column: string): string => {
    switch (column) {
      case 'last':
        return 'lastOccurrenceAt';
      case 'path':
        return 'pathToAlarm';
      case 'sourceAlarmState':
        return 'bureauAlarmState';
      case 'ack':
        return 'localAck';
      default:
        return column;
    }
  };

  const handleSort = (column: string) => {
    const nextOrder: 'asc' | 'desc' = sortColumn === column
      ? (sortOrder === 'asc' ? 'desc' : 'asc')
      : 'asc';
    setSortColumn(column);
    setSortOrder(nextOrder);
    if (useClientPagination) {
      setClientPage(1);
    }
    applySort(mapSortColumnToServer(column), nextOrder);
  };

  const handleResetSort = () => {
    setSortColumn('');
    setSortOrder('asc');
    setClientPage(1);
    applySort(undefined, undefined);
  };

  const fallbackSortedData = useMemo(() => {
    const isFallbackColumn = sortColumn === 'siteName' || sortColumn === 'path';
    if (!isFallbackColumn || filteredData.length <= 1) {
      return filteredData;
    }

    const copy = [...filteredData];
    const toKey = (value: unknown): string => String(value || '').toLowerCase();

    copy.sort((a, b) => {
      let aValue = '';
      let bValue = '';

      if (sortColumn === 'siteName') {
        aValue = toKey(a.siteName || a.siteIdentifier || '');
        bValue = toKey(b.siteName || b.siteIdentifier || '');
      }

      if (sortColumn === 'path') {
        aValue = toKey(a.pathToAlarm || a.path || a.alarmIdentifier || '');
        bValue = toKey(b.pathToAlarm || b.path || b.alarmIdentifier || '');
      }

      if (aValue < bValue) {
        return sortOrder === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortOrder === 'asc' ? 1 : -1;
      }
      return 0;
    });

    return copy;
  }, [filteredData, sortColumn, sortOrder]);

  const effectiveTotalPages = useMemo(() => {
    if (!useClientPagination) {
      return totalPages;
    }
    return Math.max(1, Math.ceil(fallbackSortedData.length / SERVER_PAGE_SIZE));
  }, [useClientPagination, totalPages, fallbackSortedData.length]);

  const visibleData = useMemo(() => {
    if (!useClientPagination) {
      return fallbackSortedData;
    }
    const start = (clientPage - 1) * SERVER_PAGE_SIZE;
    return fallbackSortedData.slice(start, start + SERVER_PAGE_SIZE);
  }, [useClientPagination, fallbackSortedData, clientPage]);

  const selectedAlarms = useMemo(() => {
    return filteredData.filter(alarm => !!selectedIds[alarm.bureauAlarmId || alarm.id || 0]);
  }, [filteredData, selectedIds]);

  const displayTotalCount = useMemo(() => {
    if (hasSiteScope) {
      return filteredData.length;
    }
    // Use counts from the same response as data, not independently refreshed stats.
    const base = counts?.[selectedCategory] ?? totalCount;
    // A refreshed server total may already exclude local acknowledgements.
    const localOffset = selectedCategory === ACTIVE_NOT_ACK
      ? data.filter(alarm =>
        locallyAckedIds.has(alarm.bureauAlarmId || alarm.id || 0) &&
        alarm.bureauAlarmState === 'ActiveAlarmsNotAck' &&
        !alarm.localAck &&
        isSourceStateActive(alarm)
      ).length
      : 0;
    return Math.max(0, base - localOffset);
  }, [hasSiteScope, filteredData.length, counts, selectedCategory, totalCount, data, locallyAckedIds]);

  const hasUnackSelected = selectedAlarms.some(a => !a.localAck);

  // Track when the selected category changes so we can show a spinner inside the table card.
  const [isCategoryLoading, setIsCategoryLoading] = React.useState(false);
  React.useEffect(() => { setIsCategoryLoading(true); }, [selectedCategory]);
  React.useEffect(() => { if (!loading) { setIsCategoryLoading(false); } }, [loading]);

  // Show spinner when category is actively switching OR on initial load with no data.
  // SignalR background refreshes (same category, data already present) are excluded.
  const showLoadingSpinner = (isCategoryLoading && loading) || (loading && data.length === 0);
  // Always keep existing content mounted to avoid page-height collapse and scroll jumps.
  const showContent = data.length > 0 || (!loading && !error);

  // Handler for when alarms are acknowledged - removes them from view immediately
  const handleAcknowledged = useCallback((_count?: number, acknowledgedAlarmIds?: number[]) => {
    // Permanently hide these alarms for this session — backend may not flip localAck immediately
    if (acknowledgedAlarmIds && acknowledgedAlarmIds.length > 0) {
      setLocallyAckedIds(prev => {
        const next = new Set(prev);
        acknowledgedAlarmIds.forEach(id => next.add(id));
        return next;
      });
    }
    refresh();
  }, [refresh]);

  return (
    <>
      <BulkActionToolbar visible={anySelected && !bulkMode}>
        {hasUnackSelected && (
          <button type="button" className="btn btn__toolbar--approve" onClick={acknowledgeSelected} disabled={!anySelected}>
            Acknowledge Selected
          </button>
        )}
        <button type="button" className="btn btn__toolbar--copy" onClick={handleBulkCopy} disabled={!anySelected}>
          Copy Selected
        </button>
        <button type="button" className="btn btn__toolbar--cancel" onClick={() => { setSelectedIds({}); }} disabled={!anySelected}>
          Cancel
        </button>
      </BulkActionToolbar>

      {/* Toolbar with filters */}
      {!hideToolbar && <section className="alarm-content">
        <div className="toolbar-content">
          <div className="toolbar-content__left pt-1 pb-1">
            <h3 className="mb-1">CSH - {getBureauCategoryLabel(selectedCategory)}</h3>
            {!hideSearch && (
              <div className="toolbar-content__search">
                  <SearchQuery
                    searchQuery={searchQuery}
                    setSearchQuery={(v: React.SetStateAction<string>) => {
                      if (typeof v === 'string') {onSearchChange?.(v);}
                      else {onSearchChange?.(v(searchQuery));}
                    }}
                  />
              </div>
            )}
          </div>

          <div className="toolbar-content__right">
            <div className="alarm-controls">
              <div className="filter-container">
                {!hideFilter && <FilterBtn ref={filterBtnRef} onClick={() => setIsFilterOpen(!isFilterOpen)} isActive={isFilterActive} isDropdownOpen={isFilterOpen} />}

                {!hideFilter && <BureauFilter
                  isOpen={isFilterOpen}
                  onClose={() => setIsFilterOpen(false)}
                  selectedCategory={selectedCategory}
                  onCategoryChange={handleCategoryChange}
                  onClear={handleClearFilter}
                  categoryCounts={{
                    // prefer statsCounts (fetched per-category in this component) or use the dashboard counts for fast initial values
                    ...(statsCounts ?? {}),
                    ...(counts ?? {}),
                    // map dashboardCounts shape to category keys if present
                    ...(dashboardCounts ? {
                      'active-ack': dashboardCounts.activeAck ?? 0,
                      [ACTIVE_NOT_ACK]: dashboardCounts.activeUnack ?? 0,
                      'not-active-not-ack': dashboardCounts.inactiveUnack ?? 0,
                      'all': (dashboardCounts.activeAck ?? 0) + (dashboardCounts.activeUnack ?? 0) + (dashboardCounts.inactiveUnack ?? 0),
                    } : {}),
                  }}
                />}
              </div>
              {!tableOnly && <div className="btn-layout-options">
                <ListViewBtn onClick={() => setGridView(false)} addClass={gridView ? '' : 'active'} />
                <GridViewBtn onClick={() => setGridView(true)} addClass={gridView ? 'active' : ''} />
              </div>}
            </div>
          </div>
        </div>
      </section>}

      {successMessage && <StatusMessage kind="success" className="mt-1">{successMessage}</StatusMessage>}
      {errorMessage && <StatusMessage kind="error" className="mt-1">{errorMessage}</StatusMessage>}

      {/* Content area */}
      {error && <p className="text-danger">{error}</p>}

      {showContent && showGridView && (
        <>
          {showLoadingSpinner && (
            <div className="table-container mb-1">
              <LoadingSpinner message="Loading alarms…" />
            </div>
          )}
          <div className="row gap-1 mb-1 mt-1">
            <BureauCategoryCards 
              filteredData={visibleData} 
              selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
              onSelect={onSelect} 
              onAcknowledged={handleAcknowledged}
              onSuccess={msg => {
                setSuccessMessage(msg);
                setTimeout(() => setSuccessMessage(null), TOAST_SUCCESS_MS);
              }}
              onError={msg => {
                setErrorMessage(msg);
                setTimeout(() => setErrorMessage(null), TOAST_ERROR_MS);
              }}
            />
          </div>
          {effectiveTotalPages > 1 && (
            <div className="row mb-1">
              <Pagination 
                currentPage={useClientPagination ? clientPage : page}
                totalPages={effectiveTotalPages}
                onPageChange={useClientPagination ? setClientPage : goToPage}
              />
            </div>
          )}
        </>
      )}

      {showContent && !showGridView && (
        <>
          <section className="alarm-table">
              <BureauCategoryTable 
               filteredData={visibleData} 
               page={useClientPagination ? clientPage : page}
               pageSize={SERVER_PAGE_SIZE}
               totalCount={displayTotalCount}
               selectedIds={selectedIds}
              onToggleSelect={handleToggleSelect}
              onToggleSelectAll={handleToggleSelectAll}
              onSelect={onSelect}
              onAcknowledged={handleAcknowledged}
              sortColumn={sortColumn}
              sortOrder={sortOrder}
              onSort={handleSort}
              onResetSort={handleResetSort}
              showSortSummary={showSortSummary}
              isLoading={showLoadingSpinner}
              headerActions={tableHeaderActions}
              headerSubtitle={tableHeaderSubtitle}
            />
          </section>
          {effectiveTotalPages > 1 && (
            <Pagination 
              currentPage={useClientPagination ? clientPage : page}
              totalPages={effectiveTotalPages}
              onPageChange={useClientPagination ? setClientPage : goToPage}
            />
          )}
        </>
      )}

      {!showContent && showLoadingSpinner && (
        <section className="alarm-table">
          <div className="table-container">
            <LoadingSpinner message="Loading alarms…" />
          </div>
        </section>
      )}

      {/* Bulk Acknowledge Modal */}
      <Modal
        isOpen={bulkMode === 'acknowledge' && selectedAlarms.length > 0}
        title={`Acknowledge ${selectedAlarms.length} Alarm(s)`}
        content={
          <div>
            <BureauAcknowledgeForm
              selectedAlarms={selectedAlarms}
              onAcknowledge={async alarms => {
                await handleBulkAcknowledge(alarms);
                setBulkMode(null);
              }}
              actionInFlight={actionInFlight}
              onRemoveSelected={handleRemoveBulkSelection}
              onClose={() => {
                setBulkMode(null);
                setSelectedIds({});
              }}
              isInsideModal={true}
            />
          </div>
        }
        onConfirm={async () => {
          await handleBulkAcknowledge(selectedAlarms);
          setBulkMode(null);
        }}
        onCancel={() => {
          setBulkMode(null);
          setSelectedIds({});
        }}
      />
    </>
  );
};
